// src/templates/products-archive/products-archive.js

/**
 * Hoffmaster Products Archive Module
 *
 * Handles:
 * - Filter accordion toggle
 * - Filter radio changes with 1-second debounce
 * - AJAX product loading
 * - URL parameter management
 * - Pagination
 * - Sorting
 * - Loading states
 * - Request cancellation
 */

class HoffmasterProductsArchive {
	constructor($widget) {
		this.$widget = $widget;
		this.elements = this.getElements();
		this.init();
		this.bindEvents();
	}

	getElements() {
		return {
			$filterHeaders: this.$widget.find('.hm-products-archive__filter-item__header'),
			$filterRadios: this.$widget.find('.hm-products-archive__filter-radio'),
			$clearFilters: this.$widget.find('.hm-products-archive__clear-filters'),
			$sortSelect: this.$widget.find('.hm-products-archive__sort-select'),
			$productsContainer: this.$widget.find('.hm-products-archive__products'),
			$paginationContainer: this.$widget.find('.hm-products-archive__pagination'),
			$resultCount: this.$widget.find('.hm-products-archive__result-count'),
			$filtersContainer: this.$widget.find('.hm-products-archive__filters-container'),
			$filtersMobileToggle: this.$widget.find('.hm-products-archive__filters-mobile-button'),
			$filtersClose: this.$widget.find('.hm-products-archive__filters-close'),
			$selectedFiltersContainer: this.$widget.find('.hm-products-archive__selected-filters'),
			$loading: this.$widget.find('.hm-products-archive__loading'),
		};
	}

	init() {
		// Initialize state
		this.currentFilters = this.getFiltersFromRadios();
		this.currentSort = this.$widget.data('current-sort') || 'date_desc';
		this.currentPage = parseInt(this.$widget.data('current-page')) || 1;
		this.postsPerPage = parseInt(this.$widget.data('posts-per-page')) || 12;
		this.restUrl = this.$widget.data('rest-url');
		this.restNonce = this.$widget.data('rest-nonce');

		// Debounce timer
		this.filterDebounceTimer = null;
		this.filterDebounceDelay = 1000; // 1 second

		// Abort controller for cancelling requests
		this.abortController = null;
	}

	bindEvents() {
		// Accordion toggle
		this.elements.$filterHeaders.on('click', (e) => this.toggleAccordion(e));

		// Filter radios with debounce and immediate mobile close
		this.elements.$filterRadios.on('change', () => {
			this.scheduleFilterUpdate();
			// Close drawer immediately on mobile when filter is selected
			if (this.elements.$filtersContainer.hasClass('hm-products-archive__filters-container--active')) {
				this.closeFiltersDrawer();
			}
		});

		// Clear filters
		this.elements.$clearFilters.on('click', () => this.clearAllFilters());

		// Sort change
		this.elements.$sortSelect.on('change', () => this.handleSortChange());

		// Pagination - delegated events
		this.$widget.on('click', '.hm-products-archive__pagination-number', (e) =>
			this.handlePaginationClick(e)
		);
		this.$widget.on('click', '.hm-products-archive__pagination-button', (e) =>
			this.handlePaginationClick(e)
		);

		// Selected filter removal - delegated events
		this.$widget.on('click', '.hm-products-archive__selected-filter', (e) =>
			this.removeSelectedFilter(e)
		);

		// Mobile filters toggle
		this.elements.$filtersMobileToggle.on('click', () => {
			const isActive = this.elements.$filtersContainer.hasClass('hm-products-archive__filters-container--active');
			if (isActive) {
				this.closeFiltersDrawer();
			} else {
				this.openFiltersDrawer();
			}
		});

		// Mobile filters close button
		this.elements.$filtersClose.on('click', () => {
			this.closeFiltersDrawer();
		});
	}

	/**
	 * Open filters drawer (mobile)
	 */
	openFiltersDrawer() {
		this.elements.$filtersContainer.addClass('hm-products-archive__filters-container--active');
		// Elevate parent section z-index to override header
		jQuery('.product-archive-father').addClass('hm-drawer-active');
		// Prevent body scroll when drawer is open
		jQuery('body').addClass('hm-drawer-open');
	}

	/**
	 * Close filters drawer (mobile)
	 */
	closeFiltersDrawer() {
		this.elements.$filtersContainer.removeClass('hm-products-archive__filters-container--active');
		// Remove elevated z-index from parent section
		jQuery('.product-archive-father').removeClass('hm-drawer-active');
		// Re-enable body scroll when drawer is closed
		jQuery('body').removeClass('hm-drawer-open');
	}

	/**
	 * Toggle accordion item
	 */
	toggleAccordion(e) {
		const $button = jQuery(e.currentTarget);
		const $filterItem = $button.closest('.hm-products-archive__filter-item');
		const $options = $button.next('.hm-products-archive__filter-item__options');
		const isExpanded = $button.attr('aria-expanded') === 'true';

		$button.attr('aria-expanded', !isExpanded);
		$filterItem.toggleClass('hm-products-archive__filter-item--active', !isExpanded);
		$options.slideToggle(300);
	}

	/**
	 * Schedule filter update with debounce
	 */
	scheduleFilterUpdate() {
		// Clear existing timer
		if (this.filterDebounceTimer) {
			clearTimeout(this.filterDebounceTimer);
		}

		// Set new timer
		this.filterDebounceTimer = setTimeout(() => {
			this.handleFilterChange();
		}, this.filterDebounceDelay);
	}

	/**
	 * Handle filter checkbox change
	 */
	handleFilterChange() {
		// Cancel any pending request
		if (this.abortController) {
			this.abortController.abort();
		}

		this.currentFilters = this.getFiltersFromRadios();
		this.currentPage = 1; // Reset to page 1 when filters change
		this.updateProducts();
	}

	/**
	 * Get filters from radio button state
	 */
	getFiltersFromRadios() {
		const filters = {};

		this.elements.$filterRadios.each((index, radio) => {
			const $radio = jQuery(radio);
			if ($radio.is(':checked')) {
				const taxonomy = $radio.data('taxonomy');
				const term = $radio.data('term');

				// With radio buttons, only one value per taxonomy
				filters[taxonomy] = [term];
			}
		});

		return filters;
	}

	/**
	 * Clear all filters
	 */
	clearAllFilters() {
		// Uncheck all radio buttons
		this.elements.$filterRadios.prop('checked', false);

		// Clear filters state
		this.currentFilters = {};
		this.currentPage = 1;

		// Reload page without filters
		window.location.href = window.location.pathname;
	}

	/**
	 * Remove a single selected filter
	 */
	removeSelectedFilter(e) {
		const $button = jQuery(e.currentTarget);
		const taxonomy = $button.data('taxonomy');
		const term = $button.data('term');

		// Uncheck corresponding radio button
		const $radio = this.elements.$filterRadios.filter(
			`[data-taxonomy="${taxonomy}"][data-term="${term}"]`
		);
		$radio.prop('checked', false);

		// Update filters
		this.handleFilterChange();
	}

	/**
	 * Handle sort change
	 */
	handleSortChange() {
		this.currentSort = this.elements.$sortSelect.val();
		this.currentPage = 1; // Reset to page 1 when sort changes
		this.updateProducts();
	}

	/**
	 * Handle pagination click
	 */
	handlePaginationClick(e) {
		const $button = jQuery(e.currentTarget);
		const page = parseInt($button.data('page'));

		if (page && page !== this.currentPage) {
			this.currentPage = page;
			this.updateProducts();

			// Scroll to top of products
			jQuery('html, body').animate(
				{
					scrollTop: this.$widget.offset().top - 100,
				},
				300
			);
		}
	}

	/**
	 * Update products via AJAX
	 */
	async updateProducts() {
		// Cancel any pending request
		if (this.abortController) {
			this.abortController.abort();
		}

		// Create new abort controller
		this.abortController = new AbortController();

		// Show loading state
		this.showLoading();

		// Build URL with query parameters
		const url = new URL(this.restUrl);
		url.searchParams.set('filters', JSON.stringify(this.currentFilters));
		url.searchParams.set('sort', this.currentSort);
		url.searchParams.set('paged', this.currentPage);
		url.searchParams.set('posts_per_page', this.postsPerPage);

		try {
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'X-WP-Nonce': this.restNonce,
				},
				signal: this.abortController.signal,
			});

			if (!response.ok) {
				throw new Error('Network response was not ok');
			}

			const data = await response.json();

			// Update products HTML
			this.elements.$productsContainer.html(data.products);

			// Update pagination HTML
			if (data.pagination) {
				this.elements.$paginationContainer.html(data.pagination);
				this.elements.$paginationContainer.show();
			} else {
				this.elements.$paginationContainer.hide();
			}

			// Update result count
			this.elements.$resultCount.html(`${data.total} products`);

			// Update selected filters display
			this.updateSelectedFiltersDisplay();

			// Update URL
			this.updateUrl();

			// Update clear filters button visibility
			const hasFilters = Object.keys(this.currentFilters).length > 0;
			this.elements.$clearFilters.toggle(hasFilters);

			// Hide loading state
			this.hideLoading();
		} catch (error) {
			if (error.name === 'AbortError') {
				// Superseded by a newer request; nothing to do.
			} else {
				console.error('Error fetching products:', error);
				this.hideLoading();
			}
		}
	}

	/**
	 * Update selected filters display
	 */
	updateSelectedFiltersDisplay() {
		// Get all taxonomies for labels
		const taxonomies = {};
		this.$widget.find('.hm-products-archive__filter-item').each((index, item) => {
			const $item = jQuery(item);
			const taxonomy = $item.data('taxonomy');
			const label = $item.find('.hm-products-archive__filter-item__title').text();
			taxonomies[taxonomy] = label;
		});

		// Build selected filters HTML
		let html = '';

		Object.keys(this.currentFilters).forEach((taxonomy) => {
			this.currentFilters[taxonomy].forEach((termSlug) => {
				// Find term name from radio button
				const $radio = this.elements.$filterRadios.filter(
					`[data-taxonomy="${taxonomy}"][data-term="${termSlug}"]`
				);
				const termName = $radio
					.closest('.hm-products-archive__filter-option')
					.find('.hm-products-archive__filter-label')
					.text();

				html += `
					<button type="button"
						class="hm-products-archive__selected-filter"
						data-taxonomy="${taxonomy}"
						data-term="${termSlug}"
						aria-label="Remove filter: ${termName}">
						${termName}
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
							<path d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
						</svg>
					</button>
				`;
			});
		});

		this.elements.$selectedFiltersContainer.html(html);
	}

	/**
	 * Update URL with current filters and sort
	 */
	updateUrl() {
		// Build base URL (remove any existing /page/N/ segment)
		let baseUrl = window.location.pathname.replace(/\/page\/\d+\/?/, '');

		// Ensure trailing slash for WordPress consistency
		if (!baseUrl.endsWith('/')) {
			baseUrl += '/';
		}

		// Add WordPress-style pagination path if page > 1
		if (this.currentPage > 1) {
			baseUrl += `page/${this.currentPage}/`;
		}

		// Build query parameters
		const params = new URLSearchParams(window.location.search);

		// Clear existing filter params
		const allTaxonomies = [];
		this.elements.$filterRadios.each((index, radio) => {
			const taxonomy = jQuery(radio).data('taxonomy');
			if (!allTaxonomies.includes(taxonomy)) {
				allTaxonomies.push(taxonomy);
			}
		});

		allTaxonomies.forEach((taxonomy) => {
			params.delete(taxonomy);
		});

		// Add current filters
		Object.keys(this.currentFilters).forEach((taxonomy) => {
			if (this.currentFilters[taxonomy].length > 0) {
				params.set(taxonomy, this.currentFilters[taxonomy].join(','));
			}
		});

		// Add sort only if not default
		if (this.currentSort !== 'date_desc') {
			params.set('sort', this.currentSort);
		} else {
			params.delete('sort');
		}

		// Remove paged query param (using path-based pagination instead)
		params.delete('paged');

		// Build final URL
		const queryString = params.toString();
		const finalUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;

		// Update URL without reload
		window.history.pushState({}, '', finalUrl);
	}

	/**
	 * Show loading state
	 */
	showLoading() {
		this.elements.$loading.show();
		this.elements.$productsContainer.attr('aria-busy', 'true');
	}

	/**
	 * Hide loading state
	 */
	hideLoading() {
		this.elements.$loading.hide();
		this.elements.$productsContainer.attr('aria-busy', 'false');
	}
}

// Initialize products archive on DOM ready
jQuery(document).ready(() => {
	jQuery('.hm-products-archive').each((index, element) => {
		new HoffmasterProductsArchive(jQuery(element));
	});
});
